import { useRef, useMemo } from 'react';
import { useStore } from '../../../../hooks';
import { useDrivesStores } from './use-drives.hook';

/**
 * Custom hook to handle file uploads
 * @param {Object} options - Configuration options
 * @param {Function} options.onUploadComplete - Callback when upload is complete
 * @param {Function} options.onUploadStart - Callback when upload starts
 * @param {string} options.folderId - Optional folder ID to upload files to
 * @param {string} options.vaultUuid - Optional vault UUID to upload files to (for DriveWorkspace)
 * @returns {Object} - Utilities to handle file uploads
 */
export const useFileUpload = ({ onUploadComplete, onUploadStart, folderId, vaultUuid } = {}) => {
  const { documentsStore, authStore } = useStore();
  const { myWorkspacesStore, activeFolderStore, drivesViewStore } = useDrivesStores();
  const fileInputRef = useRef(null);

  // ✅ PERFORMANCE: Memoize defaultDriveId to avoid re-renders when array changes
  const drivesLength = drivesViewStore?.drivesStore?.drives?.length ?? 0;
  const defaultDriveId = useMemo(() => {
    return drivesLength > 0 ? drivesViewStore.drivesStore.drives[0]?.id : null;
  }, [drivesLength]);

  const processFiles = async (files, overrideOptions = {}) => {
    if (!files || files.length === 0) return;

    onUploadStart?.();

    try {
      // ✅ Prioritize vaultUuid: overrideOptions (explicit) > prop vaultUuid > memoized defaultDriveId
      const finalVaultUuid = overrideOptions?.vaultUuid !== undefined 
        ? overrideOptions.vaultUuid 
        : (vaultUuid || defaultDriveId);
      
      const uploadOptions = {
        vaultUuid: finalVaultUuid,
        userId: authStore.userId,
        tenantId: '58145bef-4f71-468a-bded-1823d2a08f58',
        ...overrideOptions
      };
      
      // ✅ Ensure vaultUuid is always set correctly (in case overrideOptions had undefined/null vaultUuid)
      if (!uploadOptions.vaultUuid) {
        uploadOptions.vaultUuid = finalVaultUuid;
      }

      const targetFolderId =
        overrideOptions?.folderId !== undefined
          ? overrideOptions.folderId
          : folderId;

      if (targetFolderId) {
        uploadOptions.folderId = targetFolderId;
      }

      await documentsStore.uploadMultipleFiles(files, uploadOptions);

      // ✅ If onUploadComplete is provided, let the component handle reload
      // Otherwise, reload automatically based on context
      if (onUploadComplete) {
        // Component will handle reload via callback
        onUploadComplete?.();
      } else {
        // Reload data after upload based on context (backward compatibility)
        if (targetFolderId) {
          // If uploading to a specific folder, reload that folder's contents
          const vaultUuid =
            uploadOptions.vaultUuid ??
            defaultDriveId ??
            null;
          if (vaultUuid) {
            await activeFolderStore.loadFolderContents(targetFolderId, vaultUuid);
          }
        } else {
          // If uploading to workspace root, reload workspace data
          await myWorkspacesStore.loadWorkspaceData();
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    await processFiles(files);
    
    // Clear input to allow uploading the same file again
    event.target.value = '';
  };

  const handleDrop = async (event) => {
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;

    await processFiles(files);
  };

  const openFileSelector = () => {
    // ✅ Use setTimeout to ensure the ref is properly assigned after render
    setTimeout(() => {
      if (fileInputRef.current && !fileInputRef.current.dataset.opening) {
        fileInputRef.current.dataset.opening = 'true';
        fileInputRef.current.click();
        setTimeout(() => {
          if (fileInputRef.current) {
            delete fileInputRef.current.dataset.opening;
          }
        }, 100);
      }
    }, 0);
  };

  const retryUpload = async (temporaryDocument) => {
    if (!temporaryDocument) return;

    const fileToRetry = temporaryDocument.file || temporaryDocument.originalFile;
    if (!fileToRetry) {
      console.warn('No original file available for retry:', temporaryDocument.name);
      return;
    }

    const identifier = temporaryDocument.uploadId || temporaryDocument.name;
    // Remove previous temporary entry before retrying
    
    // ✅ Merge retryOptions with current vaultUuid to ensure correct vaultUuid is used
    // Prioritize current vaultUuid (from hook) over stored retryOptions.vaultUuid
    const retryOptions = {
      ...temporaryDocument.retryOptions,
      vaultUuid: vaultUuid || temporaryDocument.retryOptions?.vaultUuid || defaultDriveId
    };
    
    documentsStore.removeTemporaryDocument(identifier);

    await processFiles([fileToRetry], retryOptions);
  };

  // Props for the file input (ref is passed separately, not in props)
  const fileInputProps = {
    type: 'file',
    multiple: true,
    accept: '.pdf,.doc,.docx,.jpg,.png,.xlsx',
    onChange: handleFileInputChange,
    style: { display: 'none' }
  };

  return {
    fileInputRef,
    fileInputProps,
    handleFileInputChange,
    handleDrop,
    openFileSelector,
    processFiles,
    retryUpload
  };
};

